import { useQuery } from "@tanstack/react-query";
import { metricsApi } from "../../../../../shared/api/metrics";
import { weatherApi } from "../../../../../shared/api/weather";
import { insightsApi } from "../../../insights/api/insights";
import { qk } from "../../../../../lib/queryKeys";
import type { ReportFilters, BreakdownDimension } from "../api/types";

/**
 * Data hooks for generated report blocks. Each binds a block's ReportFilters +
 * clientId to an existing metrics/comparison/insights/weather endpoint, so a
 * generated report always renders live, current data (matching the chart-block
 * model). Filters are passed through verbatim; the API applies tenant scoping.
 */

const withClient = (filters: ReportFilters, clientId: string | null) => ({
  ...filters,
  client_id: clientId ?? undefined,
});

/** Enabled once we have either a client scope or explicit user_ids. */
const scopeReady = (filters: ReportFilters, clientId: string | null) =>
  clientId !== undefined || !!filters.user_ids?.length;

export const useReportStats = (
  metrics: string[],
  filters: ReportFilters,
  clientId: string | null,
) => {
  const params = { ...withClient(filters, clientId), metrics };
  return useQuery({
    queryKey: qk.metrics.stats(params),
    queryFn: () => metricsApi.getStats(params),
    staleTime: 0,
    enabled: scopeReady(filters, clientId),
  });
};

export const useReportComparison = (
  dimension: BreakdownDimension,
  metrics: string[],
  filters: ReportFilters,
  clientId: string | null,
) => {
  const params = {
    ...withClient(filters, clientId),
    group_by: dimension,
    metrics,
  } as const;
  return useQuery({
    queryKey: qk.metrics.comparison(params),
    queryFn: () => metricsApi.getComparison(params),
    staleTime: 0,
    enabled: scopeReady(filters, clientId),
  });
};

export const useReportInsights = (
  filters: ReportFilters,
  clientId: string | null,
) => {
  const scopeType = filters.user_ids?.length ? "user" : "org";
  const params = {
    scope_type: scopeType as "org" | "user",
    scope_id: filters.user_ids?.[0],
    client_id: clientId ?? undefined,
    start_date: filters.start_date,
    end_date: filters.end_date,
    department: filters.department,
    age_range: filters.age_range,
    sex: filters.sex,
    shift_pattern: filters.shift_pattern,
    user_ids: filters.user_ids,
  };
  return useQuery({
    queryKey: ["reports", "insights", params],
    queryFn: () => insightsApi.getStandardInsights(params),
    staleTime: 0,
    enabled: scopeReady(filters, clientId),
  });
};

export const useReportWeather = (
  filters: ReportFilters,
  clientId: string | null,
) => {
  const params = withClient(filters, clientId);
  return useQuery({
    queryKey: qk.weatherTrends(params),
    queryFn: () => weatherApi.getTrends(params),
    staleTime: 10 * 60 * 1000,
    enabled: scopeReady(filters, clientId),
  });
};

export const useReportWeatherAnomalies = (
  filters: ReportFilters,
  clientId: string | null,
) => {
  const params = withClient(filters, clientId);
  return useQuery({
    queryKey: qk.weatherAnomalies(params),
    queryFn: () => weatherApi.getAnomalies(params),
    staleTime: 10 * 60 * 1000,
    enabled: scopeReady(filters, clientId),
  });
};
